import React from "react";

import { useLocation } from "react-router-dom";
import { isBrowser, isMobile } from "react-device-detect";

import { createAppTheme } from "./styles";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Box from "@mui/material/Box";

import MainPage from "./general/main/MainPage";
import PageLoader from "./helper-components/pages/PageLoader";
import ConfirmationDialog from "./helper-components/input/ConfirmationDialog";

import { getKeyFromQuery } from "./helper-functions/shared/pageUtils.js";
import { disableConsoleLogging } from "./helper-functions/shared/miscUtils.js";

import { useLocalStorage } from "./helper-functions/hooks/useLocalStorage";
import { useWindowDimensions } from "./helper-functions/hooks/useWindowDimensions";
import { useSnackbar } from "./helper-functions/hooks/useSnackbar";
import { useDialog } from "./helper-functions/hooks/useDialog";

import { useNavigateWithPrev } from "./helper-functions/hooks/useNavigation.js";
import { useBackendStates } from "./helper-functions/hooks/useBackendStates.js";
import { useDataGridPaginationState } from "./helper-functions/hooks/useDataGridPaginationState";

import { ExaminationProvider } from "./helper-functions/context/ExaminationContext.jsx";

import { getLanguage } from "./config/language/language";
import * as Constants from "./config/constants/Constants.js";

export const ColorModeContext = React.createContext({
    toggleColorMode: () => {}
});

export default function App() {
    // Find URL parameter: debug.
    const debug = getKeyFromQuery("debug");

    // Variables can be saved in a browser's localstorage.
    const [mode, setMode] = useLocalStorage("colorMode", "light");

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            }
        }),
        [setMode]
    );

    // The project's theme is here.
    // Can also modify all the MUI components.
    const theme = React.useMemo(() => createAppTheme(mode, isMobile), [mode]);

    // Console log some data.
    console.log(``);
    console.log(`==== ${getLanguage().WEBSITE_NAME} ====`);
    console.log(`Version: ${Constants.PAGE_VERSION}`);
    console.log(`Running in ${isMobile ? "mobile" : "desktop"} view`);
    console.log(`Development mode: ${Constants.DEVELOPMENT_ENV}`);
    console.log(`================`);
    console.log(``);

    if (!Constants.DEVELOPMENT_ENV && debug !== "true") disableConsoleLogging();

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <StyledApp />
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}

/**
 * Every single component visible to the user is inside this one.
 * This is the master-component, so to say.
 *
 * Define any variables (states) used throughout the app here.
 * All such variables will be passed to Main.js as props.
 * NB! The spread operator doesn't deep copy - use JSON parse and stringify.
 */
function StyledApp() {
    const location = useLocation();

    // Thanks to useState, <setVariable> changes <variable> dynamically.
    const [isPageLoading, setIsPageLoading] = React.useState(Constants.PAGE_ENABLE_LOADER);
    const [isConnectionToServerEstablished, setIsConnectionToServerEstablished] = React.useState(false);
    const [isLoggedIn, setIsLoggedIn] = React.useState(false); // Logged in depends on localstorage variables and JWT validity.

    const [showMainPageComponents, setShowMainPageComponents] = React.useState(true); // Allows to hide the main page components (e.g. sidebar).
    const [expand, setExpand] = React.useState(null); // Used to expand accordion(s).

    // Variables can be saved in a browser's localstorage.
    const [username, setUsername] = useLocalStorage("username", "");
    const [userJWT, setUserJWT] = useLocalStorage("jwt", "");

    // Tour guide is a step-by-step guide for new users.
    const [showInitialTourGuide, setShowInitialTourGuide] = useLocalStorage("initialTourGuide", true);
    const [runTourGuide, setRunTourGuide] = React.useState(false);
    const [tourGuideSteps, setTourGuideSteps] = React.useState([]);
    const [tourGuideStepIndex, setTourGuideStepIndex] = React.useState(0);

    // States queried from server (backend).
    const [me, setMe] = React.useState({});

    const backend = useBackendStates({ logOther: true });

    const examinations = useDataGridPaginationState("examinations", {
        logData: true,
        logPagination: true,
        initialSorting: { field: "created_on", sort: "desc" },
        columnVisibility: { patient_id: false, notes: false }
    });
    const patients = useDataGridPaginationState("patients", { logData: true, logPagination: true, initialSorting: { field: "created_on", sort: "desc" } });
    const users = useDataGridPaginationState("users", { logData: true, logPagination: true, initialSorting: { field: "last_login", sort: "desc" } });

    // States for creating a new examination live in ExaminationProvider - see the note there.

    // Custom hooks.
    const { showSnackbar } = useSnackbar();
    const { navigate } = useNavigateWithPrev();
    const { enqueueDialog, setOpen, ...options } = useDialog();

    // Roles (depends on the JWT claim).
    const [isAdmin, setIsAdmin] = React.useState(false);

    // Menu sidebar (drawer).
    const { screenHeight, screenWidth } = useWindowDimensions();
    const [openAppDrawer, setOpenAppDrawer] = React.useState(screenWidth > Constants.AUTO_OPEN_SIDEBAR_PX);
    const toggleAppDrawer = () => {
        setOpenAppDrawer(!openAppDrawer);
    };

    // Config history sidebar.
    const [openNotificationDrawer, setOpenNotificationDrawer] = React.useState(false);
    const toggleNotificationDrawer = () => {
        setOpenNotificationDrawer(!openNotificationDrawer);
    };

    return (
        // Box is a general container where every other component is inside, kind of like a div.
        // This specifc box also deals with light/dark mode.
        <Box
            sx={{
                bgcolor: "background.default",
                color: "text.primary"
            }}>
            {/* Loading spinner. */}
            <PageLoader imageSrc="/images/logo.png" show={isPageLoading} />

            {/* Actual page after loading in this div. */}
            <div hidden={isPageLoading}>
                {/* Dialog pop-up to confirm critical changes. */}
                <ConfirmationDialog
                    options={{
                        enqueueDialog,
                        setOpen,
                        ...options
                    }}
                />

                {/* Pass all props (variables) down to the child components. */}
                <ExaminationProvider>
                    <MainPage
                        backend={{
                            ...backend,
                            // Database tables
                            examinations,
                            patients,
                            users
                        }}
                        // User info.
                        user={{
                            me,
                            setMe,
                            username,
                            setUsername,
                            userJWT,
                            setUserJWT,
                            isLoggedIn,
                            setIsLoggedIn
                        }}
                        roles={{
                            isAdmin,
                            setIsAdmin
                        }}
                        browser={{
                            // Browser info.
                            navigate,
                            location,
                            screenHeight,
                            screenWidth,
                            isBrowser,
                            isMobile,
                            // Loading state.
                            isPageLoading,
                            setIsPageLoading,
                            showMainPageComponents,
                            setShowMainPageComponents,
                            expand,
                            setExpand,
                            // Tour guide.
                            showInitialTourGuide,
                            setShowInitialTourGuide,
                            runTourGuide,
                            setRunTourGuide,
                            tourGuideSteps,
                            setTourGuideSteps,
                            tourGuideStepIndex,
                            setTourGuideStepIndex
                        }}
                        server={{
                            isConnectionToServerEstablished,
                            setIsConnectionToServerEstablished
                        }}
                        // Snackbar.
                        showSnackbar={showSnackbar}
                        // Confirmation dialog.
                        showDialog={enqueueDialog}
                        setOpenDialog={setOpen}
                        // Sidebar.
                        sidebar={{
                            openAppDrawer,
                            toggleAppDrawer,
                            openNotificationDrawer,
                            toggleNotificationDrawer
                        }}
                    />
                </ExaminationProvider>
            </div>
        </Box>
    );
}
